/* eslint-disable react/prop-types */
import React, { useState, useRef, useEffect } from "react";
import CustomDateRangePicker from "../../CustomDateRangePicker";
import { Button } from "@nextui-org/button";
import { ReactComponent as ChevronDown } from "../../../assets/icons/chevron-down.svg";
import { ReactComponent as Search } from "../../../assets/icons/search.svg";
import { ReactComponent as Rotate } from "../../../assets/icons/rotate.svg";
import FilterSelector from "./FilterSelector";
import useTicketStore from "../../../stores/useTicketStore";
import { useDropdown } from "./FilterContext";
import AppUtility from "../../../assets/js/AppUtility";
import { ReactComponent as Close } from "../../../assets/icons/close.svg";
import useApproverStore from "../../../stores/useApproverStore";
import {
  GetActivityOptions,
  GetCarModelOptions,
} from "../../../services/TicketService";
import AppUserProfile, { UserType } from "../../../assets/js/AppUserProfile";
import { ParamKey, useQueryParams } from "../../../hooks/useQueryParams";

const FilterTag = ({ label, onRemove }) => {
  return (
    <div className="rounded-2xl border-1 border-gray-300 py-1 px-3 text-sm flex items-center gap-2">
      <span>{label}</span>
      {onRemove && (
        <button
          onClick={onRemove}
          className="hover:bg-gray-200 rounded-full p-1"
        >
          <Close width={12} height={12} className="text-black" />
        </button>
      )}
    </div>
  );
};

const SelectedFilter = ({ tempOtherFilter, removeOtherFilterValue }) => {
  const plainObjectToISODate = (plainObject) => {
    if (
      !plainObject ||
      !plainObject.year ||
      !plainObject.month ||
      !plainObject.day
    )
      return null;
    return `${plainObject.year}-${String(plainObject.month).padStart(
      2,
      "0"
    )}-${String(plainObject.day).padStart(2, "0")}`;
  };

  return (
    <div className="flex flex-wrap gap-3">
      {tempOtherFilter.activityPeriod.start &&
        tempOtherFilter.activityPeriod.end && (
          <FilterTag
            label={`${AppUtility.displayDatetime(
              plainObjectToISODate(tempOtherFilter.activityPeriod.start)
            )} - ${AppUtility.displayDatetime(
              plainObjectToISODate(tempOtherFilter.activityPeriod.end)
            )}`}
            onRemove={() => {
              removeOtherFilterValue("activityPeriod");
            }}
          />
        )}
      {tempOtherFilter.dealerName &&
        tempOtherFilter.dealerName.map((dealer) => (
          <FilterTag
            key={dealer.id}
            label={dealer.name}
            onRemove={() => {
              removeOtherFilterValue("dealerName", dealer.id);
            }}
          />
        ))}
      {tempOtherFilter.dealerCode &&
        tempOtherFilter.dealerCode.map((dealer) => (
          <FilterTag
            key={dealer.id}
            label={dealer.name}
            onRemove={() => {
              removeOtherFilterValue("dealerCode", dealer.id);
            }}
          />
        ))}
      {tempOtherFilter.carModel &&
        tempOtherFilter.carModel.map((carModel) => (
          <FilterTag
            key={carModel.id}
            label={carModel.name}
            onRemove={() => {
              removeOtherFilterValue("carModel", carModel.id);
            }}
          />
        ))}
      {tempOtherFilter.activityType &&
        tempOtherFilter.activityType.map((activity) => (
          <FilterTag
            key={activity.id}
            label={activity.name}
            onRemove={() => {
              removeOtherFilterValue("activityType", activity.id);
            }}
          />
        ))}
    </div>
  );
};

const OtherFilterDropDown = ({ isDisabled }) => {
  const dropdownRef = useRef(null);
  const { activeDropdownId, toggleDropdown, closeDropdown } = useDropdown();
  const { approvers } = useApproverStore();

  const dropdownId = "otherFilter";
  const isOpen = activeDropdownId === dropdownId;

  const [carModels, setCarModels] = useState([]);
  const [activities, setActivities] = useState([]);
  const hasFormattedOnInit = useRef(false);
  const isFromUrlRestore = useRef(false);

  const [tempOtherFilter, setTempOtherFilter] = useState({
    dealerName: [],
    dealerCode: [],
    carModel: [],
    activityType: [],
    activityPeriod: {
      start: null,
      end: null,
    },
  });

  const userType = AppUserProfile.getData()["whoami"];

  const queryParams = useQueryParams();

  const {
    otherFilter,
    setBulkOtherFilters,
    resetOtherFilters,
    formatOtherFilterPayload,
    clearOtherFilterUrlParams,
  } = useTicketStore();

  useEffect(() => {
    setTempOtherFilter({ ...otherFilter });
  }, [otherFilter]);

  useEffect(() => {
    const getCarModel = async () => {
      const [activityResponse, carModelsResponse] = await Promise.all([
        GetActivityOptions(),
        GetCarModelOptions(),
      ]);

      setCarModels(carModelsResponse?.data.car_model_options ?? []);
      setActivities(activityResponse?.data.activity_options ?? []);
    };

    getCarModel();
  }, []);

  useEffect(() => {
    const urlOtherFilter = queryParams.getParam(ParamKey.OtherFilter);

    if (urlOtherFilter && !hasFormattedOnInit.current) {
      isFromUrlRestore.current = true;
    }
  }, [queryParams]);

  useEffect(() => {
    if (
      isFromUrlRestore.current &&
      !hasFormattedOnInit.current &&
      hasActiveFilters()
    ) {
      formatOtherFilterPayload();
      hasFormattedOnInit.current = true;
      isFromUrlRestore.current = false;
    }
  }, [otherFilter, formatOtherFilterPayload]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, closeDropdown]);

  const handleTempOtherFilterValue = (filterKey, value) => {
    setTempOtherFilter((prevState) => {
      if (!(filterKey in prevState)) return prevState;

      let newOtherFilter = { ...prevState };

      if (value && value.isSelectAll) {
        if (value.selectNone) {
          newOtherFilter[filterKey] = [];
        } else if (value.options && Array.isArray(value.options)) {
          const currentValues = Array.isArray(prevState[filterKey])
            ? [...prevState[filterKey]]
            : [];

          const existingIds = new Set(currentValues.map((item) => item.id));

          const newValues = [
            ...currentValues,
            ...value.options.filter((option) => !existingIds.has(option.id)),
          ];

          newOtherFilter[filterKey] = newValues;
        }
      } else if (filterKey === "activityPeriod") {
        newOtherFilter.activityPeriod = {
          start: value?.start || null,
          end: value?.end || null,
        };
      } else if (Array.isArray(prevState[filterKey])) {
        const currentValues = [...prevState[filterKey]];
        const valueIndex = currentValues.findIndex(
          (item) => item.id === value.id
        );

        if (valueIndex >= 0) {
          currentValues.splice(valueIndex, 1);
        } else {
          currentValues.push(value);
        }

        newOtherFilter[filterKey] = currentValues;
      }

      return newOtherFilter;
    });
  };

  const handleTempRemoveOtherFilterValue = (filterKey, filterId = null) => {
    setTempOtherFilter((prevState) => {
      if (filterKey === "activityPeriod") {
        return {
          ...prevState,
          activityPeriod: { start: null, end: null },
        };
      }

      if (Array.isArray(prevState[filterKey])) {
        if (filterId === null) {
          return {
            ...prevState,
            [filterKey]: [],
          };
        }

        const updatedValues = prevState[filterKey].filter(
          (item) => item.id !== filterId
        );

        return {
          ...prevState,
          [filterKey]: updatedValues,
        };
      }

      return prevState;
    });
  };

  const handleResetFilters = () => {
    const emptyFilter = {
      dealerName: [],
      dealerCode: [],
      carModel: [],
      activityType: [],
      activityPeriod: {
        start: null,
        end: null,
      },
    };

    setTempOtherFilter(emptyFilter);
    resetOtherFilters(queryParams);
    hasFormattedOnInit.current = false;
    isFromUrlRestore.current = false;

    setTimeout(() => {
      clearOtherFilterUrlParams(queryParams);
    }, 100);
  };

  const toggleDropdownHandler = () => {
    if (!isDisabled) {
      toggleDropdown(dropdownId);
    }
  };

  const [dropdownPosition, setDropdownPosition] = useState({
    top: "full",
    left: "0",
  });

  const calculateDropdownPosition = () => {
    if (!dropdownRef.current) return;

    const button = dropdownRef.current.querySelector("button");
    if (!button) return;

    const buttonRect = button.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const estimatedDropdownWidth = 600;

    if (buttonRect.left + estimatedDropdownWidth > viewportWidth) {
      setDropdownPosition({ top: "full", left: "auto", right: "0" });
    } else {
      setDropdownPosition({ top: "full", left: "0", right: "auto" });
    }
  };

  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        calculateDropdownPosition();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen]);

  const hasActiveFilters = () => {
    return (
      otherFilter.dealerName.length > 0 ||
      otherFilter.dealerCode.length > 0 ||
      otherFilter.carModel.length > 0 ||
      otherFilter.activityType.length > 0 ||
      (otherFilter.activityPeriod.start && otherFilter.activityPeriod.end)
    );
  };

  const hasTempActiveFilters = () => {
    return (
      tempOtherFilter.dealerName.length > 0 ||
      tempOtherFilter.dealerCode.length > 0 ||
      tempOtherFilter.carModel.length > 0 ||
      tempOtherFilter.activityType.length > 0 ||
      (tempOtherFilter.activityPeriod.start &&
        tempOtherFilter.activityPeriod.end)
    );
  };

  const countSelectedFilters = () => {
    let count = 0;
    count += otherFilter.dealerName.length;
    count += otherFilter.dealerCode.length;
    count += otherFilter.carModel.length;
    count += otherFilter.activityType.length;

    if (otherFilter.activityPeriod.start && otherFilter.activityPeriod.end) {
      count += 1;
    }

    return count;
  };

  const handleSearchClick = () => {
    setBulkOtherFilters(tempOtherFilter, queryParams);

    setTimeout(() => {
      formatOtherFilterPayload();
    }, 0);

    closeDropdown();
    hasFormattedOnInit.current = true;
  };

  const selectedFilterCount = countSelectedFilters();

  return (
    <div className="flex flex-col lg:w-fit w-full">
      <div
        className="flex flex-row gap-x-4 items-center relative"
        ref={dropdownRef}
      >
        <Button
          className="text-left truncate w-full lg:w-fit border bg-white text-black border-gray-300 hover:bg-gray-100"
          endContent={
            <div className="flex items-center gap-2">
              {selectedFilterCount > 0 && (
                <div className="flex items-center justify-center min-w-5 h-5 rounded-full bg-blue-600 text-white text-xs">
                  <span>{selectedFilterCount}</span>
                </div>
              )}
              <ChevronDown
                width={10}
                className={`transition-transform duration-200 ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </div>
          }
          isDisabled={isDisabled}
          onPress={toggleDropdownHandler}
        >
          ตัวกรองอื่นๆ
        </Button>

        {isOpen && (
          <div
            className="absolute mt-1 z-50 bg-white shadow-lg border border-gray-200 rounded-xl px-3 py-4 divide-y-1 w-full lg:w-auto"
            style={{
              top:
                dropdownPosition.top === "full" ? "100%" : dropdownPosition.top,
              left: dropdownPosition.left,
              right: dropdownPosition.right,
            }}
          >
            <div className="p-2 w-auto">
              <p>ตัวกรองอื่นๆ</p>
              <div className="flex flex-col pt-4">
                <div className="flex gap-x-5">
                  <CustomDateRangePicker
                    label={"ช่วงเวลาที่จัดกิจกรรม"}
                    variant={"bordered"}
                    showMonthAndYearPickers={true}
                    width="lg:min-w-52"
                    onChange={(e) => {
                      handleTempOtherFilterValue(
                        "activityPeriod",
                        e.target.value
                      );
                    }}
                    value={
                      tempOtherFilter.activityPeriod?.start &&
                      tempOtherFilter.activityPeriod?.end
                        ? tempOtherFilter.activityPeriod
                        : null
                    }
                    hideLabel
                  />

                  {userType !== UserType.DEALER_BYD &&
                    userType !== UserType.DEALER_DENZA && (
                      <FilterSelector
                        id="dealerName"
                        label={"Dealer Code - ชื่อดีลเลอร์"}
                        options={[...approvers.values()]
                          .flat()
                          .filter(
                            (item) =>
                              item.role_name === UserType.DEALER_BYD ||
                              item.role_name === UserType.DEALER_DENZA
                          )
                          .map((item) => {
                            return {
                              id: item.id,
                              name: `${item.code} - ${item.name}`,
                            };
                          })}
                        onChange={(value) =>
                          handleTempOtherFilterValue("dealerName", value)
                        }
                        selectedValue={tempOtherFilter.dealerName}
                        buttonWidth={"min-w-52"}
                        searchAble
                        isMultiSelect
                        isNested
                      />
                    )}

                  <FilterSelector
                    id="carModel"
                    label={"รุ่นรถยนต์"}
                    options={carModels?.map((item) => ({
                      id: item,
                      name: item,
                    }))}
                    onChange={(value) =>
                      handleTempOtherFilterValue("carModel", value)
                    }
                    selectedValue={tempOtherFilter.carModel}
                    buttonWidth={"min-w-52"}
                    isMultiSelect
                    searchAble
                    isNested
                  />
                </div>
              </div>
              <div className="flex flex-col pt-4">
                <div className="flex gap-x-5">
                  <FilterSelector
                    id="activityType"
                    label={"ลักษณะกิจกรรม"}
                    searchAble
                    options={activities?.map((item) => ({
                      id: item,
                      name: item,
                    }))}
                    onChange={(value) =>
                      handleTempOtherFilterValue("activityType", value)
                    }
                    selectedValue={tempOtherFilter.activityType}
                    buttonWidth={"min-w-52"}
                    isMultiSelect
                    isNested={true}
                  />
                </div>
              </div>
              <div className="py-3 flex justify-end gap-x-4">
                <Button
                  className="bg-white text-black"
                  size="sm"
                  variant=""
                  startContent={<Rotate width={15} className="text-black" />}
                  onPress={handleResetFilters}
                >
                  Reset
                </Button>
                <Button
                  className="bg-black text-white"
                  size="sm"
                  startContent={<Search width={17} className="text-white" />}
                  onPress={handleSearchClick}
                >
                  ค้นหา
                </Button>
              </div>
            </div>
            {hasTempActiveFilters() && (
              <div className="py-5">
                <SelectedFilter
                  tempOtherFilter={tempOtherFilter}
                  removeOtherFilterValue={handleTempRemoveOtherFilterValue}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OtherFilterDropDown;
